import { useStore } from '@tanstack/react-form';
import { Card, Checkbox, LearnMoreLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { SETTING_KEY_TO_LABEL } from '../../constants';
import { useCustomerGroupBuilderForm } from '../../hooks/use-customer-group-builder-form';
import {
  SettingAddressManagementBaseSchema,
  type SettingAddressManagementState,
} from '../../types/customer-group.schema';
import { ResetSettingsButton } from './reset-settings-button';

type AddressManagementKey = keyof SettingAddressManagementState;

const LANGUAGE_STRING_LOOKUP = {
  allowAddressEditing: 'allow_address_editing',
  allowDefaultBillingModification: 'allow_default_billing_modification',
} as const satisfies Record<AddressManagementKey, string>;

export function AddressManagementCard() {
  const form = useCustomerGroupBuilderForm();

  const isAddressManagementEnabled = useStore(
    form.store,
    (state) => state.values.settings.allowAddressEditing?.allowAddressEditing,
  );

  return (
    <Card showBorder>
      <Card.Header collapsible headingLevel="h2" icon="document">
        {SETTING_KEY_TO_LABEL.allowAddressEditing}
      </Card.Header>
      <Card.Body>
        <Text variant="secondary">
          {t('features:customer_group.components.address_management_card.description')}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/customers#p8vQZ" />
        </Text>
        {Object.keys(SettingAddressManagementBaseSchema.shape).map((key) => (
          <form.Field
            key={key}
            name={`settings.allowAddressEditing.${key as AddressManagementKey}`}
            listeners={{
              onChange: ({ value, fieldApi }) => {
                if ((key as AddressManagementKey) === 'allowAddressEditing' && value === false) {
                  fieldApi.form.setFieldValue(
                    'settings.allowAddressEditing.allowDefaultBillingModification',
                    false,
                  );
                }
              },
            }}
          >
            {(field) => (
              <Checkbox
                label={t(
                  `features:customer_group.components.address_management_card.${LANGUAGE_STRING_LOOKUP[key as AddressManagementKey]}.label`,
                )}
                checked={field.state.value}
                onChange={() => field.handleChange((prev) => !prev)}
                disabled={
                  (key as AddressManagementKey) === 'allowDefaultBillingModification' &&
                  !isAddressManagementEnabled
                }
                description={t(
                  `features:customer_group.components.address_management_card.${LANGUAGE_STRING_LOOKUP[key as AddressManagementKey]}.description`,
                )}
              />
            )}
          </form.Field>
        ))}
        <div className="flex justify-start">
          <form.Field name="settings.allowAddressEditing">
            {(field) => <ResetSettingsButton field={field} />}
          </form.Field>
        </div>
      </Card.Body>
    </Card>
  );
}
